// Step 3 — Sector-keuze (T13). Multi-select uit de MSI-meetstandaard.
// Vervangt de oude binaire "standaard / op maat" keuze.
//
// Flow per context:
//   - Geen partner-deeplink + geen netwerk-deeplink → toon alle sectoren uit
//     de meetstandaard-api.
//   - Sector-netwerk-deeplink (geen partner) → toon dezelfde lijst maar
//     pre-checked op basis van `SubscribedMSISectors` van de netwerk-tenant.
//     Gebruiker mag uitvinken/toevoegen.
//   - Partner-deeplink → STAP WORDT GESKIPT door getStepSequence (partner-TOC
//     overrijdt sector-keuze).
//
// Op submit: schrijft `SelectedSectors` op CompagnyMeta + lastStep:4.

import { useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { OnboardingFlow as OnboardingFlowContext } from '../../../StateManagment/OnboardingFlow'
import { db } from '../../../firebase/config'
import useDevPreview from '../../../hooks/useDevPreview'
import useAvailableSectors from '../../../hooks/useAvailableSectors'
import useCompagnyMetaById from '../../../hooks/useCompagnyMetaById'
import { getSectorInfo } from '../../../helpers/meetstandaard/sectorCatalog'
import parseDeeplink from '../../../helpers/onboardingHelpers/parseDeeplink'
import spinner from '../../../images/spinner-ripple.svg'

const Step3MeasurementApproach = ({ onNext, onPrev }) => {

    // Context
    const {
        compagnyId,
        selectedSectors,
        setSelectedSectors,
    } = useContext(OnboardingFlowContext)

    // Sectoren uit meetstandaard-api, aangevuld met de sectoren die wij lokaal al
    // kennen — anders is energiearmoede hier niet te kiezen (ADR-031).
    const { selectableSectors: availableSectors, loading: sectorsLoading, error: sectorsError } = useAvailableSectors()

    // Sector-netwerk-deeplink → pre-check sectoren uit `SubscribedMSISectors`
    const location = useLocation()
    const deeplink = useMemo(() => parseDeeplink(location.search), [location.search])
    const { compagnyMeta: networkTenant } = useCompagnyMetaById(deeplink.networkCompagnyID)
    const networkSectors = useMemo(() => {
        const list = networkTenant?.SubscribedMSISectors
        return Array.isArray(list) ? list : []
    }, [networkTenant])

    // Lokale draft — initialiseer 1× wanneer er nog niets in context staat:
    //   - context-waarde wint (terug-navigeren)
    //   - daarna network-default
    //   - daarna leeg
    const [draft, setDraft] = useState(() => selectedSectors || [])
    const [draftSeeded, setDraftSeeded] = useState(false)

    useEffect(() => {
        if (draftSeeded) return
        if (selectedSectors && selectedSectors.length > 0) {
            setDraft(selectedSectors)
            setDraftSeeded(true)
            return
        }
        if (networkSectors.length > 0) {
            setDraft(networkSectors)
            setDraftSeeded(true)
        }
    }, [selectedSectors, networkSectors, draftSeeded])

    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)

    const { active: devPreview } = useDevPreview()

    const toggleSector = (slug) => {
        setDraft((prev) => prev.includes(slug)
            ? prev.filter((s) => s !== slug)
            : [...prev, slug]
        )
    }

    const persist = async (sectorsToSave) => {

        // Dev-preview: alleen state + advance
        if (devPreview) {
            setSelectedSectors(sectorsToSave)
            onNext()
            return
        }

        if (!compagnyId) {
            setError('Geen actieve onboarding-sessie. Begin opnieuw bij Step 1.')
            return
        }

        setLoading(true)
        try {
            const snap = await db.collection('CompagnyMeta')
                .where('CompagnyID', '==', compagnyId)
                .limit(1)
                .get()

            if (snap.empty) throw new Error('CompagnyMeta niet gevonden.')

            await snap.docs[0].ref.update({
                SelectedSectors: sectorsToSave,
                OnboardingStatus: { lastStep: 4, completed: false },
            })

            setSelectedSectors(sectorsToSave)
            onNext()
        } catch (err) {
            console.error('Step3 persist error', err)
            setError(err.message || 'Er ging iets mis. Probeer het opnieuw.')
            setLoading(false)
        }
    }

    const submit = (e) => {
        e.preventDefault()
        setError('')
        persist(draft)
    }

    return (
        <div className="onboarding-step">
            <h1>Welke standaarden zijn voor jou relevant?</h1>
            <p className="onboarding-step-intro">
                Kies één of meerdere thema's uit de Meetstandaard Social Impact als jouw
                standaard-set — niets kiezen mag ook. Je kunt later in de software altijd
                andere standaarden (bij)kiezen.
            </p>

            {sectorsLoading && (
                <div className="onboarding-step-loading">
                    <img src={spinner} alt="Sectoren laden…" />
                </div>
            )}

            {sectorsError && (
                <p className="onboarding-error">
                    Kon de meetstandaard niet ophalen: {sectorsError}.
                </p>
            )}

            {!sectorsLoading && availableSectors.length > 0 && (
                <div className="onboarding-sector-list">
                    {availableSectors.map((slug) => {
                        const info = getSectorInfo(slug)
                        const checked = draft.includes(slug)
                        const fromNetwork = networkSectors.includes(slug)

                        return (
                            <label
                                key={slug}
                                className={`onboarding-sector-row ${checked ? 'checked' : ''}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleSector(slug)}
                                />
                                <div className="onboarding-sector-info">
                                    <h2>
                                        {info.label}
                                        {fromNetwork && (
                                            <span className="onboarding-sector-network-tag">
                                                Voorgesteld door je netwerk
                                            </span>
                                        )}
                                    </h2>
                                    {info.description && (
                                        <p>{info.description}</p>
                                    )}
                                </div>
                            </label>
                        )
                    })}

                    <label
                        className={`onboarding-sector-row ${draft.length === 0 ? 'checked' : ''}`}
                    >
                        <input
                            type="checkbox"
                            checked={draft.length === 0}
                            onChange={() => setDraft([])}
                        />
                        <div className="onboarding-sector-info">
                            <h2>Geen</h2>
                            <p>Ik heb geen behoefte aan een standaard of ik kies later zelf.</p>
                        </div>
                    </label>
                </div>
            )}

            {error && <p className="onboarding-error">{error}</p>}

            <div className="onboarding-step-actions">
                <button
                    type="button"
                    className="onboarding-button-secondary"
                    onClick={onPrev}
                    disabled={loading}
                >
                    Terug
                </button>

                <button type="button" onClick={submit} disabled={loading}>
                    {loading ? <span className="onboarding-button-spinner" aria-label="Bezig" /> : 'Volgende'}
                </button>
            </div>
        </div>
    )

}

export default Step3MeasurementApproach
